import { useState, useEffect, useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { auth } from "../utils/firebase";
import thanq from "../api/thanq";
import Table from "../components/Table";
import TipDetails from "../components/TipDetails";
import moment from "moment/moment";
import { Paper, Button, Grid, TextField, Tooltip, Typography, Box, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { CSVLink } from "react-csv";
const columns = [
    {
        name: "TrxnId",
        field: "tipId",
        cell: (row) => row.id,
    },
    {
        name: "Razoypay OrderId",
        field: "orderId",
        cell: (row) => row.orderId,
    },
    {
        name: "Amount",
        field: "amount",
        cell: (row) => `₹${Number(row.amount)}/-`,
        selector: (row) => row.amount,
        sortable: true,
    },
    {
        name: "Transaction Status",
        field: "transactionStatus",
        cell: (row) => row.transactionStatus,
        selector: (row) => row.transactionStatus,
        sortable: true,
    },
    {
        name: "Payout Status",
        field: "payoutStatus",
        cell: (row) => row.payoutStatus,
        selector: (row) => row.payoutStatus,
        sortable: true,
    },
    {
        name: "Created at",
        field: "createdAt",
        cell: (row) => moment(row.createdTime).format("YYYY-MM-DD HH:mm:ss"),
        selector: (row) => row.createdTime,
        sortable: true
    },
];

const csvHeaders = [
    {
        label: "Amount",
        key: "amount",
    },
    {
        label: "UPI Address",
        key: "upi",
    },
    {
        label: "Account Number",
        key: "accountNumber",
    },
    {
        label: "IFSC Code",
        key: "ifsc",
    },
];

const statuses = [
    "INITIATED",
    "CANCELLED",
    "FAILED",
    "COMPLETED"
]

const Tips = () => {
    const [date, setDate] = useState(moment().subtract(1, "days").format("YYYY-MM-DD"));
    const [csvData, setCSVData] = useState([]);
    const [tips, setTips] = useState([]);
    const [csvLoading, setCSVLoading] = useState(true);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [transactionStatusFilter, setTransactionStatusFilter] = useState("all");
    const [payoutStatusFilter, setPayoutStatusFilter] = useState("all");
    const [amountFilter, setAmountFilter] = useState("all");
    const [selectedTip, setSelectedTip] = useState(null);
    const [tipDetailsOpen, setTipDetailsOpen] = useState(false);
    const phoneNumber = auth.currentUser?.phoneNumber?.replace("+91", "") || "";

    useEffect(() => {
        fetchTips();
        // eslint-disable-next-line
    }, []);

    const fetchTips = async () => {
        try {
            setLoading(true);
            const response = await thanq.get(`/admin/tips`);
            setTips(response?.data?.tips || []);
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error" });
        } finally {
            setLoading(false);
            setCSVLoading(false);
        }
    };

    const getDates = useCallback(() => {
        return {
            from: `${moment(date).startOf("day").unix()}000`,
            to: `${moment(date).endOf("day").unix()}999`,
        };
    }, [date]);

    const handleCSVDownload = () => {
        setCSVLoading(true);
        thanq
            .post("/payment/get-csv-details", getDates())
            .then((res) => {
                const details = res?.data?.details || [];
                setCSVData(details);
                if (details.length === 0) {
                    enqueueSnackbar("No items for given date", { variant: "info" });
                }
            })
            .catch((err) => {
                enqueueSnackbar(err.message, { variant: "error" });
            })
            .finally(() => setCSVLoading(false));
    };

    const handleReset = () => {
        setCSVLoading(true);
        thanq
            .post("/payment/payout-status", { ...getDates(), status: "INITIATED", phoneNumber })
            .then((res) => {
                enqueueSnackbar("Reset payment status for given date", { variant: "success" });
            })
            .catch((err) => {
                enqueueSnackbar(err.message, { variant: "error" });
            })
            .finally(() => {
                fetchTips();
            });
    };

    const handleCompletion = () => {
        setCSVLoading(true);
        thanq
            .post("/payment/payout-status", { ...getDates(), status: "COMPLETED", phoneNumber })
            .then((res) => {
                enqueueSnackbar("Updated the status for date", { variant: "success" });
            })
            .catch((err) => {
                enqueueSnackbar(err.message, { variant: "error" });
            })
            .finally(() => {
                fetchTips();
            });
    };

    const handleRowClick = (tip) => {
        setSelectedTip(tip);
        setTipDetailsOpen(true);
    };

    const handleCloseTipDetails = () => {
        setTipDetailsOpen(false);
        setSelectedTip(null);
    };

    const handleTipUpdate = (details) => {
        enqueueSnackbar("Still to be implemented")
    }

    const filteredTips = tips.filter((tip) => {
        const searchMatch = searchTerm === "" ||
            tip.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tip.amount?.toString().includes(searchTerm) ||
            tip.transactionStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            tip.payoutStatus?.toLowerCase().includes(searchTerm.toLowerCase());

        const transactionMatch = transactionStatusFilter === "all" ||
            tip.transactionStatus === transactionStatusFilter;

        const payoutMatch = payoutStatusFilter === "all" ||
            tip.payoutStatus === payoutStatusFilter;

        let amountMatch = true;
        if (amountFilter !== "all") {
            const amount = Number(tip.amount);
            switch (amountFilter) {
                case "0-100":
                    amountMatch = amount >= 0 && amount <= 100;
                    break;
                case "100-500":
                    amountMatch = amount > 100 && amount <= 500;
                    break;
                case "500-1000":
                    amountMatch = amount > 500 && amount <= 1000;
                    break;
                case "1000+":
                    amountMatch = amount > 1000;
                    break;
                default:
                    amountMatch = true;
            }
        }

        return searchMatch && transactionMatch && payoutMatch && amountMatch;
    });

    return (
        <div>
            <br />
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Search & Filter Tips
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Search Tips"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by ID, amount, status..."
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Transaction Status</InputLabel>
                            <Select
                                value={transactionStatusFilter}
                                label="Transaction Status"
                                onChange={(e) => setTransactionStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Statuses</MenuItem>
                                {statuses.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payout Status</InputLabel>
                            <Select
                                value={payoutStatusFilter}
                                label="Payout Status"
                                onChange={(e) => setPayoutStatusFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Statuses</MenuItem>
                                {statuses.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Amount Range</InputLabel>
                            <Select
                                value={amountFilter}
                                label="Amount Range"
                                onChange={(e) => setAmountFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Amounts</MenuItem>
                                <MenuItem value="0-100">₹0 - ₹100</MenuItem>
                                <MenuItem value="100-500">₹100 - ₹500</MenuItem>
                                <MenuItem value="500-1000">₹500 - ₹1000</MenuItem>
                                <MenuItem value="1000+">₹1000+</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSearchTerm("");
                                setTransactionStatusFilter("all");
                                setPayoutStatusFilter("all");
                                setAmountFilter("all");
                            }}
                            sx={{ mt: 1 }}
                        >
                            Clear All Filters
                        </Button>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, width: "fit-content" }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {filteredTips.length} of {tips.length} tips
                        {searchTerm && ` • Search: "${searchTerm}"`}
                        {transactionStatusFilter !== "all" && ` • Transaction: ${transactionStatusFilter}`}
                        {payoutStatusFilter !== "all" && ` • Payout: ${payoutStatusFilter}`}
                        {amountFilter !== "all" && ` • Amount: ${amountFilter}`}
                    </Typography>
                </Box>
            </Paper>

            <Table
                loading={loading}
                columns={columns}
                data={filteredTips}
                title="Tips"
                handleRowClick={handleRowClick}
            />

            <TipDetails
                tip={selectedTip}
                open={tipDetailsOpen}
                setOpen={handleCloseTipDetails}
                onUpdateTip={handleTipUpdate}
            />

            <br />
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid
                    spacing={2}
                    container
                    direction={"column"}
                    sx={{ mb: 2 }}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Grid
                        spacing={2}
                        container
                        direction={"row"}
                        sx={{ mb: 2 }}
                        justifyContent={"center"}
                        alignItems={"center"}
                    >
                        <Typography variant="h5">Get Bulk Tips for</Typography>
                        <TextField
                            type="date"
                            value={date}
                            onChange={(e) => {
                                setDate(e.target.value);
                                setCSVData([]);
                            }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    {!csvLoading ? (
                        <Grid
                            container
                            direction={"row"}
                            sx={{ mb: 2 }}
                            justifyContent={"center"}
                            alignItems={"center"}
                        >
                            <Button
                                variant="outlined"
                                disabled={csvLoading}
                                onClick={handleCSVDownload}
                                color={csvData?.length > 0 ? "success" : "primary"}
                            >
                                {csvData?.length > 0 ? (
                                    <CSVLink
                                        data={csvData}
                                        headers={csvHeaders}
                                        filename={`tips-${date}.csv`}
                                        style={{ textDecoration: "none", color: "inherit" }}
                                        target="_blank"
                                    >
                                        Download CSV
                                    </CSVLink>
                                ) : (
                                    "Generate CSV"
                                )}
                            </Button>
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        Resets the status for the selected date back to INITIATED
                                        <Typography fontWeight={"bold"}>
                                            ONLY Click when CSV was lost or NOT uploaded
                                        </Typography>
                                    </Typography>
                                }
                                placement="top"
                            >
                                <Button
                                    variant="contained"
                                    disabled={csvLoading}
                                    onClick={handleReset}
                                    color={"error"}
                                >
                                    Reset for date
                                </Button>
                            </Tooltip>
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        Sets the status for the selected date to COMPLETED
                                        <Typography fontWeight={"bold"}>
                                            ONLY Click when CSV is uploaded and initiated
                                        </Typography>
                                    </Typography>
                                }
                                placement="top"
                            >
                                <Button
                                    variant="contained"
                                    disabled={csvLoading}
                                    onClick={handleCompletion}
                                    color={"success"}
                                >
                                    Completed for date
                                </Button>
                            </Tooltip>
                        </Grid>
                    ) : (
                        <div>Performing action...</div>
                    )}
                </Grid>
            </Paper>
        </div>
    );
};

export default Tips;
